import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import FolderIcon from "@mui/icons-material/Folder";
import LayersIcon from "@mui/icons-material/Layers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VnvAssistRecentRuns from "./VnvAssistRecentRuns";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import DatasetSelectorModal from "src/reusable/Chat/PromptInput/DatasetSelectorModal";
import SelectedDatasetsContainer, {
  type Dataset,
} from "src/reusable/Chat/PromptInput/SelectedDatasetsContainer";
import VnvAssistInputDocumentPanel from "./VnvAssistInputDocumentPanel";
import { PageTopHelpButton } from "src/components/help";

type VnvAssistSplashScreenProps = {
  onStart: () => Promise<boolean>;
  isLoading: boolean;
  error?: string;
};

const VnvAssistSplashScreen = ({
  onStart,
  isLoading,
  error,
}: VnvAssistSplashScreenProps) => {
  const {
    selectedArtifact,
    selectedDatasets,
    selectedDatasetIds,
    setSelectedDatasets,
  } = useVnvAssistStore();

  const [isStarting, setIsStarting] = useState(false);
  const [showDatasetModal, setShowDatasetModal] = useState(false);

  const canStart = !!selectedArtifact && selectedDatasetIds.length > 0;

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await onStart();
    } finally {
      setIsStarting(false);
    }
  };

  const handleRemoveDataset = (datasetId: number) => {
    setSelectedDatasets(selectedDatasets.filter((d) => d.id !== datasetId));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        p: 3,
        gap: 3,
      }}
    >
      {/* Help button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          mb: 1,
        }}
      >
        <PageTopHelpButton productLabel="V&V Assist" product="vnv-assist" />
      </Box>

      {/* Header */}
      <Stack alignItems="center" spacing={1} mt={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <img
            src="/images/nuclearn-n-full.png"
            alt="Nuclearn Logo"
            width={40}
            height={40}
          />
          <Typography variant="h4" fontWeight={700}>
            VNV Assist
          </Typography>
        </Stack>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          maxWidth={520}
        >
          Verify &amp; validate factual claims in your documents against
          reference material.
        </Typography>
      </Stack>

      {/* Two-panel setup */}
      <Container maxWidth="lg" disableGutters>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {/* Left: Reference Datasets */}
          <Paper
            variant="outlined"
            sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column" }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
              <Typography variant="body1" fontWeight="medium">
                Reference Datasets
              </Typography>
              <Tooltip title="Select one or more datasets containing source material. The AI will search these datasets to find evidence that supports or contradicts the claims in your input document.">
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>

            {selectedDatasets.length > 0 && (
              <Box mb={1.5}>
                <SelectedDatasetsContainer
                  datasets={selectedDatasets}
                  onRemove={handleRemoveDataset}
                  icon={<LayersIcon fontSize="small" color="primary" />}
                />
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<FolderIcon />}
              onClick={(e) => {
                e.currentTarget.blur();
                setShowDatasetModal(true);
              }}
              sx={{ textTransform: "none", alignSelf: "flex-start" }}
              size="small"
            >
              {selectedDatasets.length > 0
                ? "Add / Change Datasets"
                : "Select Datasets"}
            </Button>
          </Paper>

          {/* Right: Input Document */}
          <Paper
            variant="outlined"
            sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column" }}
          >
            <VnvAssistInputDocumentPanel />
          </Paper>
        </Stack>
      </Container>

      {error && (
        <Alert severity="error" sx={{ width: "100%", maxWidth: 780 }}>
          {error}
        </Alert>
      )}

      {/* Start Analysis */}
      <Button
        variant="contained"
        size="large"
        disabled={!canStart || isLoading || isStarting}
        onClick={handleStart}
        sx={{ minWidth: 200 }}
        startIcon={
          isStarting || isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : undefined
        }
      >
        {isStarting || isLoading ? "Starting..." : "Start Analysis"}
      </Button>

      {/* Recent runs */}
      <Box sx={{ width: "100%", maxWidth: 780 }}>
        <VnvAssistRecentRuns limit={3} />
      </Box>

      {/* Reference dataset selector modal */}
      <DatasetSelectorModal
        open={showDatasetModal}
        onClose={() => setShowDatasetModal(false)}
        defaultSelectedDatasets={selectedDatasets}
        onConfirm={(datasets: Dataset[]) => {
          setSelectedDatasets(datasets);
          setShowDatasetModal(false);
        }}
      />
    </Box>
  );
};

export default VnvAssistSplashScreen;
